import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Button } from "../../../ui/Button";
import { displayToast } from "../../../app/AppToast";
import { useCurrentUser } from "../../../../domains/user/hooks/useCurrentUser";
import { useLogOutUser } from "../../../../domains/user/hooks/useLogOutUser";
import { apiClient } from "../../../../services/api";
import { gatEventUserDeleteAccount } from "../../../../tools/analytics/domains/user.analytics";
import {
  closeSettings,
  popSettingsView,
} from "../../../../domains/app/stores/settingsSheetStore";
import { SettingsSubHeader } from "../SettingsSubHeader";

export function DeleteConfirmView(): React.JSX.Element {
  const { t } = useTranslation();
  const { currentUser } = useCurrentUser();
  const logout = useLogOutUser();
  // useForm only for a submit-in-flight flag without owning `useState`.
  const { handleSubmit, formState } = useForm();

  async function onDelete(): Promise<void> {
    if (currentUser === null) return;
    try {
      await apiClient.delete("/webapp/private/users/me", {});
      gatEventUserDeleteAccount(currentUser.uid);
      displayToast(t("Account deleted"), "success");
      closeSettings();
      await logout({ skipApiCalls: true });
    } catch {
      displayToast(t("Something went wrong. Please try again."), "error");
    }
  }

  return (
    <div className="flex h-full flex-col">
      <SettingsSubHeader title={t("Delete account")} />
      <div className="flex flex-1 flex-col overflow-y-auto scroll-fog-y px-5">
        <h3 className="text-ui-highlight font-semibold text-ds-black">
          {t("Second bite at the cherry 🍒")}
        </h3>
        <p className="mt-2 text-ui-note text-ds-slate-600">
          {t("Do you really want to permanently delete your account?")}
        </p>
      </div>
      <form
        className="shrink-0 flex flex-col gap-3 px-5 pt-2 pb-6"
        onSubmit={handleSubmit(() => {
          void onDelete();
        })}
      >
        <Button
          type="button"
          variant="secondary"
          size="lg"
          className="w-full"
          onClick={popSettingsView}
        >
          {t("Cancel")}
        </Button>
        <Button
          type="submit"
          variant="destructive"
          size="lg"
          className="w-full"
          disabled={formState.isSubmitting}
        >
          {t("Delete account")}
        </Button>
      </form>
    </div>
  );
}
